<template>
  <div class="user-page">
    <v-card class="user-card">
      <div class="user-header">
        <div class="user-header__copy">
          <div class="user-title">Users</div>
          <div class="user-subtitle">Manage user accounts and access.</div>
        </div>
        <v-text-field
            v-model="search"
            class="user-search"
            append-icon="mdi-magnify"
            hide-details
            label="Search"
            single-line
        ></v-text-field>
        <v-btn v-if="canCreateUser" class="user-add-button" depressed @click="registerUser()">
          <v-icon left>mdi-plus-circle</v-icon>
          <span>Create User</span>
        </v-btn>
      </div>
      <v-data-table
          :headers="headers"
          :items="users.rows"
          :loading="loading"
          :options.sync="options"
          :server-items-length="users.count"
          class="user-table elevation-1"
          loading-text="Loading... please wait"
      >
        <template v-slot:[`item.fullName`]="{ item }">
          <label for="item">{{
              item.firstName ? `${item.firstName} ${item.middleName} ${item.lastName}` : "-"
            }}</label>
        </template>
        <template v-slot:[`item.username`]="{ item }">
          <label for="item">{{
              item.username ? item.username : "-"
            }}</label>
        </template>
        <template v-slot:[`item.email`]="{ item }">
          <label for="item">{{
              item.email ? item.email : "-"
            }}</label>
        </template>
        <template v-slot:[`item.phoneNumber`]="{ item }">
          <label for="item">{{
              item.phoneNumber ? item.phoneNumber : "-"
            }}</label>
        </template>
        <template v-slot:[`item.isActive`]="{ item }">
          <v-chip :color="getColor(item.isActive)" dark>
            {{ item.isActive == true ? "Active" : "Not Active" }}
          </v-chip>
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon
                  class="mr-2 user-action-icon"
                  @click="viewUser(item)"
                  v-on="on"
              >mdi-eye
              </v-icon
              >
            </template>
            <span>View User</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-if="canEditUser" v-slot:activator="{ on }">
              <v-icon
                  class="mr-2 user-action-icon"
                  @click="editUser(item)"
                  v-on="on"
              >mdi-circle-edit-outline
              </v-icon
              >
            </template>
            <span>Edit User</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card>

    <template>
      <v-dialog v-model="dialog" persistent width="60%">
        <v-card class="user-dialog-card">
          <v-card-title
              class="user-dialog__title"
              primary-title
          >{{ title }}

            <v-spacer></v-spacer>

            <v-btn class="user-dialog__close" icon @click="closeDialog()">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title
          >
          <v-divider></v-divider>
          <HrdAddUser
              v-if="dialog"
              :user="user"
              @close="closeDialog()"
              @registerOrUpdateUser="registerOrUpdateUser"
          ></HrdAddUser>
        </v-card>
      </v-dialog>
    </template>
    <template>
      <v-dialog v-model="detailDialog" persistent width="60%">
        <v-card class="user-dialog-card">
          <v-card-title
              class="user-dialog__title"
              primary-title
          >{{ title }}

            <v-spacer></v-spacer>

            <v-btn class="user-dialog__close" icon @click="closeDialog()">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title
          >
          <v-divider></v-divider>
          <HrdUserDetail
              v-if="detailDialog"
              :user="user"
          ></HrdUserDetail>
        </v-card>
      </v-dialog>
    </template>
  </div>
</template>


<script>
import HrdAddUser from "./hrd-add-user";
import {UserService} from "./user.service";
import HrdUserDetail from "./hrd-user-detail.vue";
import {getUser} from "@/utils/helper";

export default {
  components: {
    HrdAddUser,
    HrdUserDetail
  },

  data() {
    return {
      dialog: false,
      detailDialog: false,
      title: "",
      options: {
        page: 1,
        itemsPerPage: 10,
      },
      search: "",
      searchDebounce: null,
      users: {
        rows: [],
        count: 0,
      },
      headers: [
        {text: "Full Name", value: "fullName"},
        {text: "User Name", value: "username"},
        {text: "Email", value: "email"},
        {text: "Phone Number", value: "phoneNumber"},
        {text: "User's Status", value: "isActive", align: "center"},
        {text: "Actions", value: "actions", sortable: false, align: "center"},
      ],
      loading: false,
      user: null,
    };
  },
  async beforeMount() {
    this.fetchUserList();
  },
  watch: {
    options: {
      handler() {
        this.fetchUserList();
      },
      deep: true,
    },
    search: function (val) {
      if (this.searchDebounce) {
        clearTimeout(this.searchDebounce);
        this.searchDebounce = null;
      }
      const text = (val || "").toString().trim();
      if (text.length === 0) {
        this.fetchUserList();
        return;
      }
      if (text.length < 3) {
        return;
      }
      this.searchDebounce = setTimeout(() => {
        this.fetchUserList();
      }, 400);
    },
  },
  computed: {
    canCreateUser() {
      return getUser().permissions.some(
          (permission) =>
              permission.menuItem.menuItemName == "Users" && permission.canCreate
      );
    },
    canEditUser() {
      return getUser().permissions.some(
          (permission) =>
              permission.menuItem.menuItemName == "Users" && permission.canUpdate
      );
    },
  },
  methods: {
    getErrorMessage(error, fallbackMessage) {
      return error?.response?.data?.message || fallbackMessage;
    },
    async fetchUserList() {
      this.loading = true;
      try {
        const {page, itemsPerPage} = this.options;
        const res = await UserService.getUsersWP(
            page,
            itemsPerPage,
            this.search
        );

        if (res.status === 200) {
          this.users = res.data;
          return;
        }

        this.$toast.error(
            "There was a problem fetching users, Please try Again!"
        );
      } catch (error) {
        this.$toast.error(
            this.getErrorMessage(
                error,
                "There was a problem fetching users, Please try Again!"
            )
        );
      } finally {
        this.loading = false;
      }
    },
    viewUser(item) {
      this.user = item;
      this.detailDialog = true;
      this.title = "User Detail";
    },
    registerUser() {
      this.user = null;
      this.dialog = true;
      this.title = "Create User";
    },
    editUser(item) {
      this.user = item;
      this.dialog = true;
      this.title = "Edit User";
    },
    closeDialog() {
      this.dialog = false;
      this.detailDialog = false;
      this.fetchUserList();
    },
    async registerOrUpdateUser(user) {
      try {
        let res = null;

        if (user.editMode) {
          res = await UserService.update(user.id, user);

          if (res.status === 201 || res.status === 200) {
            switch (res.data.status) {
              case 200:
                this.$toast.success(res.data.message);
                this.closeDialog();
                break;
              case 500:
                this.$toast.error(res.data.message);
                break;
              default:
                this.$toast.error("Unable to update user, please try again");
                break;
            }
          } else {
            this.$toast.error("Unable to update user, please try again");
          }
        } else {
          res = await UserService.create(user);
          if (res.status === 200 || res.status === 201) {
            switch (res.data.status) {
              case 200:
                this.$toast.success(res.data.message);
                this.closeDialog();
                break;
              case 500:
                this.$toast.error(res.data.message);
                break;
              default:
                this.$toast.error("Unable to create user, please try again");
                break;
            }
          } else {
            this.$toast.error("Unable to create user, please try again");
          }
        }
      } catch (error) {
        this.$toast.error(
            this.getErrorMessage(
                error,
                user.editMode
                    ? "Unable to update user, please try again"
                    : "Unable to create user, please try again"
            )
        );
      }
    },
    getColor(isActive) {
      if (isActive == false) return "red";
      else return "green";
    },
  },
};
</script>
<style scoped>
.user-page {
  padding: 24px;
  background: #f5f7fb;
  min-height: 100%;
}

.user-card {
  border-radius: 0 0 12px 12px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  background: #ffffff;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.06);
  overflow: hidden;
}

.user-header {
  display: grid;
  grid-template-columns: minmax(220px, 1fr) minmax(240px, 360px) auto;
  align-items: center;
  gap: 16px;
  padding: 24px 28px;
}

.user-header__copy {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.user-title {
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
}

.user-subtitle {
  font-size: 14px;
  color: #475569;
}

.user-search {
  width: 100%;
  max-width: 360px;
  justify-self: center;
  align-self: center;
}

.user-table {
  border-radius: 0 0 12px 12px;
}

.user-dialog-card {
  border-radius: 10px;
  overflow: hidden;
}

.user-dialog__title {
  display: flex;
  align-items: center;
  gap: 16px;
  background: #3a4f63;
  color: #ffffff;
  padding: 20px 24px;
}

.user-dialog__close {
  color: #ffffff !important;
}

.user-dialog__close .v-icon {
  color: #ffffff !important;
}

.user-add-button {
  background: #1976d2 !important;
  border-color: #1976d2 !important;
  color: #ffffff !important;
  text-transform: none;
  font-weight: 600;
}

.user-action-icon {
  color: #1976d2 !important;
}

@media (max-width: 700px) {
  .user-page {
    padding: 12px;
  }

  .user-header {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    padding: 20px;
  }

  .user-search {
    max-width: 100%;
  }

  .user-add-button {
    width: 100%;
    justify-content: center;
  }
}
</style>
